import type { AuthError, MFASession, SendPasswordResetEmailResponse } from '@st/sdk'
import { Effect } from 'effect'
import { match } from '@st/pattern'
import { Analytics, Auth, ErrorReporting, Routing, RuntimeConfig, Snackbar } from '~/effects'
import type { User } from './auth-client-adapter'

export type PasswordResetEmailError =
  | NonNullable<SendPasswordResetEmailResponse['error']>
  | 'request_failed'

export type ActiveMfaSession = MFASession & { sessionId: string }

export type AuthOperationOutcome =
  | { type: 'loginFailed'; error: AuthError }
  | { type: 'mfaRequired'; session: ActiveMfaSession }
  | { type: 'mfaVerificationFailed'; error: AuthError }
  | { type: 'passwordResetSucceeded' }
  | { type: 'passwordResetFailed'; error: AuthError }
  | { type: 'passwordResetEmailSent'; email: string }
  | { type: 'passwordResetEmailFailed'; error: PasswordResetEmailError }

export function loginWithPassword(email: string, password: string) {
  return Effect.gen(function* () {
    const auth = yield* Auth
    const result = yield* auth.loginWithEmailPassword(email, password)

    return yield* match(result)
      .returnType<Effect.Effect<AuthOperationOutcome | null>>()
      .with({ status: 'mfaRequired' }, ({ session }) =>
        requireActiveMfaSession(session).pipe(
          Effect.map((session) => ({ type: 'mfaRequired', session }) as const)
        )
      )
      .with({ status: 'succeeded' }, () => Effect.succeed(null))
      .with({ status: 'failed' }, ({ error }) =>
        Effect.succeed({ type: 'loginFailed', error } as const)
      )
      .exhaustive()
  })
}

function requireActiveMfaSession(session: MFASession): Effect.Effect<ActiveMfaSession> {
  if (!session.sessionId) {
    return Effect.die(new Error('MFA challenge has no session ID'))
  }
  return Effect.succeed({ ...session, sessionId: session.sessionId })
}

export function completeMfaVerification(sessionId: string, code: string) {
  return Effect.gen(function* () {
    const auth = yield* Auth
    const result = yield* auth.completeLoginWithMFACode(sessionId, code)

    return match(result)
      .returnType<AuthOperationOutcome | null>()
      .with({ status: 'succeeded' }, () => null)
      .with({ status: 'failed' }, ({ error }) => ({ type: 'mfaVerificationFailed', error }))
      .exhaustive()
  })
}

export function loginWithMagicLink(magicLinkToken: string, cleanedPath: string) {
  return Effect.gen(function* () {
    const auth = yield* Auth
    const routing = yield* Routing

    yield* routing.replacePath(cleanedPath)
    return yield* auth.loginWithMagicLink(magicLinkToken).pipe(
      Effect.as(null),
      Effect.catchTag('AuthFailure', (error) =>
        Effect.succeed({ type: 'loginFailed', error } as const)
      )
    )
  })
}

export function resetPassword(args: {
  token: string
  password: string
  passwordConfirmation: string
  name?: string
  cleanedPath: string
}) {
  return Effect.gen(function* () {
    const auth = yield* Auth
    const routing = yield* Routing
    const snackbar = yield* Snackbar
    const result = yield* auth.resetPassword(args.token, args.password, args.passwordConfirmation)

    if (!result.ok) {
      return { type: 'passwordResetFailed', error: result.error } as const
    }

    const name = args.name?.trim()
    if (name && result.value.user) {
      // Password reset has already succeeded. Profile failure has its own UX.
      yield* auth.setProfileName(result.value.user.id, name).pipe(
        Effect.catchTag('AuthFailure', () =>
          snackbar.show('Password reset, but failed to update full name', {
            variant: 'error'
          })
        )
      )
    }

    yield* routing.replacePath(args.cleanedPath)
    return { type: 'passwordResetSucceeded' } as const
  })
}

export function sendPasswordResetEmail(email: string) {
  return Effect.gen(function* () {
    const auth = yield* Auth
    const config = yield* RuntimeConfig
    const response = yield* auth.sendPasswordResetEmail(email, config.browserOrigin)

    if (response.error) {
      return { type: 'passwordResetEmailFailed', error: response.error } as const
    }
    return { type: 'passwordResetEmailSent', email } as const
  }).pipe(
    Effect.catchTag('AuthFailure', () =>
      Effect.succeed({ type: 'passwordResetEmailFailed', error: 'request_failed' } as const)
    )
  )
}

export function logout() {
  return Effect.gen(function* () {
    const auth = yield* Auth
    yield* auth.logout()
    return null
  })
}

export function identifyAuthenticatedUser(user: User) {
  return Effect.gen(function* () {
    const analytics = yield* Analytics
    const errorReporting = yield* ErrorReporting
    yield* analytics.trackUserIdentified(user)
    yield* errorReporting.setUser({ id: user.id, email: user.email })
    return null
  })
}

export function identifyAnonymousUser() {
  return Effect.gen(function* () {
    const analytics = yield* Analytics
    const errorReporting = yield* ErrorReporting
    yield* analytics.trackUserAnonymous()
    yield* errorReporting.setUser(null)
    return null
  })
}
